<template>
  <div class="onboarding-legal-consents">
    <p class="text-subtitle-2 font-weight-bold mb-3">
      {{ t('onboarding.wizard.review.acceptances-intro') }}
    </p>

    <div
      v-if="requiresAgb"
      class="mb-3"
    >
      <v-checkbox
        v-model="acceptAgb"
        data-ai-field="acceptAgb"
        class="onboarding-legal-consents__checkbox"
        color="primary"
        density="comfortable"
        hide-details
        :error="showErrors && !acceptAgb"
        :disabled="disabled"
      >
        <template #label>
          <span class="text-body-2 onboarding-legal-consents__label">
            {{ t('onboarding.submit.acceptTerms') }}
            <a
              v-if="publicTOCandLicenceUrl"
              class="text-primary"
              target="_blank"
              rel="noopener noreferrer"
              :href="publicTOCandLicenceUrl"
              @click.stop
            >
              {{ t('onboarding.submit.termsOf', { company: legalCompanyName }) }}
            </a>
            <span
              class="text-error"
              aria-hidden="true"
              >*</span
            >
          </span>
        </template>
      </v-checkbox>
    </div>

    <!-- US accepts the privacy policy with the signup consents. -->
    <div
      v-if="!isUsOnboarding"
      class="mb-3"
    >
      <v-checkbox
        v-model="acceptPrivacy"
        data-ai-field="acceptPrivacy"
        class="onboarding-legal-consents__checkbox"
        color="primary"
        density="comfortable"
        hide-details
        :error="showErrors && !acceptPrivacy"
        :disabled="disabled"
      >
        <template #label>
          <span class="text-body-2 onboarding-legal-consents__label">
            {{ t('onboarding.submit.acceptPrivacy') }}
            <a
              v-if="dataPrivacyUrl"
              class="text-primary"
              target="_blank"
              rel="noopener noreferrer"
              :href="dataPrivacyUrl"
              @click.stop
            >
              {{ t('onboarding.submit.privacyPolicyOf', { company: legalCompanyName }) }}
            </a>
            <span v-else>
              {{ t('onboarding.submit.privacyPolicyOf', { company: legalCompanyName }) }}
            </span>
            <template v-if="privacyEndSuffix"> {{ ' ' }}{{ privacyEndSuffix }} </template>
            <span
              class="text-error"
              aria-hidden="true"
              >*</span
            >
          </span>
        </template>
      </v-checkbox>
    </div>

    <div
      v-if="requiresGuideline"
      class="mb-3"
    >
      <v-checkbox
        v-model="acceptGuideline"
        data-ai-field="acceptGuideline"
        class="onboarding-legal-consents__checkbox"
        color="primary"
        density="comfortable"
        hide-details
        :error="showErrors && !acceptGuideline"
        :disabled="disabled"
      >
        <template #label>
          <span class="text-body-2 onboarding-legal-consents__label">
            {{ t('onboarding.submit.acceptGuideline') }}
            <a
              v-if="guidelineUrl"
              class="text-primary"
              :href="guidelineUrl"
              target="_blank"
              rel="noopener noreferrer"
              @click.stop
            >
              {{ t('onboarding.submit.guidelineFor', { platform: readablePlatformName }) }}
            </a>
            <span v-else>
              {{ t('onboarding.submit.guidelineFor', { platform: readablePlatformName }) }}
            </span>
            <template v-if="guidelineReceivedSuffix">
              {{ ' ' }}{{ guidelineReceivedSuffix }}
            </template>
            <span
              class="text-error"
              aria-hidden="true"
              >*</span
            >
          </span>
        </template>
      </v-checkbox>
    </div>

    <div
      v-if="isUsOnboarding"
      class="mb-3"
    >
      <v-checkbox
        v-model="authorizeSendOnBehalf"
        data-ai-field="authorizeSendOnBehalf"
        data-testid="onboarding-consent-send-on-behalf"
        class="onboarding-legal-consents__checkbox"
        color="primary"
        density="comfortable"
        hide-details
        :error="showErrors && !authorizeSendOnBehalf"
        :disabled="disabled"
      >
        <template #label>
          <span class="text-body-2 onboarding-legal-consents__label"
            >{{ t('onboarding.submit.authorizeSendOnBehalf')
            }}<span
              class="text-error"
              aria-hidden="true"
              >*</span
            ></span
          >
        </template>
      </v-checkbox>
    </div>

    <div class="mb-3">
      <v-checkbox
        v-model="contactViaEmail"
        class="onboarding-legal-consents__checkbox"
        color="primary"
        density="comfortable"
        hide-details
        :disabled="disabled"
      >
        <template #label>
          <span class="text-body-2 onboarding-legal-consents__label">{{
            t('onboarding.submit.contactViaEmail')
          }}</span>
        </template>
      </v-checkbox>
    </div>

    <div class="mb-3">
      <v-checkbox
        v-model="contactViaWhatsapp"
        class="onboarding-legal-consents__checkbox"
        color="primary"
        density="comfortable"
        hide-details
        :disabled="disabled"
      >
        <template #label>
          <span class="text-body-2 onboarding-legal-consents__label">{{
            t('onboarding.submit.contactViaWhatsapp')
          }}</span>
        </template>
      </v-checkbox>
    </div>

    <v-alert
      v-if="showSupportHint"
      class="mb-3"
      type="info"
      variant="tonal"
      density="comfortable"
    >
      <template #title>
        <span class="text-body-2 font-weight-bold">
          {{ t('onboarding.submit.supportAndTipsTitle') }}
        </span>
      </template>
      <template #text>
        <span class="text-body-2">
          {{ t('onboarding.submit.supportAndTipsDescription') }}
        </span>
      </template>
      <template #prepend />
    </v-alert>

    <div class="onboarding-legal-consents__last">
      <v-checkbox
        v-model="acceptConfirmData"
        data-ai-field="acceptConfirmData"
        class="onboarding-legal-consents__checkbox"
        color="primary"
        density="comfortable"
        hide-details
        :error="showErrors && !acceptConfirmData"
        :disabled="disabled"
      >
        <template #label>
          <!-- No whitespace before the asterisk: it must not wrap onto its own line. -->
          <span class="text-body-2 onboarding-legal-consents__label"
            >{{ t('onboarding.submit.confirmAllData')
            }}<span
              class="text-error"
              aria-hidden="true"
              >*</span
            ></span
          >
        </template>
      </v-checkbox>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { useBranding } from '@/composables/useBranding'
import useFeature from '@/composables/useFeature'
import featureNames from '@/lib/featureNames'
import { getMaterialGuideUrl } from '@/lib/materialGuideUrl.js'

export type LegalConsentsValues = {
  acceptAgb: boolean
  acceptPrivacy: boolean
  acceptGuideline: boolean
  /** US only — required there instead of `acceptPrivacy`. */
  authorizeSendOnBehalf: boolean
  contactViaEmail: boolean
  contactViaWhatsapp: boolean
  acceptConfirmData: boolean
}

const props = defineProps<{
  modelValue: LegalConsentsValues
  /** Company being onboarded — its license-agreement state decides whether AGB is shown. */
  company?: { id?: string | null; licenseAgreement?: { acceptedAt?: string | null } | null } | null
  disabled?: boolean
  /** Mark unticked required consents as errors (after a blocked continue). */
  showErrors?: boolean
  /** US: send-on-behalf authorization replaces the privacy acknowledgement. */
  isUsOnboarding?: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: LegalConsentsValues]
}>()

const { t, te, locale } = useI18n()
const { isFeatureActive, getFeature } = useFeature()
const {
  legalCompanyName,
  readablePlatformName,
  dataPrivacyUrl,
  publicTOCandLicenceUrl,
  isPlatformSmg,
} = useBranding()

/** One writable proxy per field so a v-model edit emits a merged update. */
const field = <K extends keyof LegalConsentsValues>(key: K) =>
  computed<boolean>({
    get: () => Boolean(props.modelValue?.[key]),
    set: (value) => emit('update:modelValue', { ...props.modelValue, [key]: value }),
  })

const acceptAgb = field('acceptAgb')
const acceptPrivacy = field('acceptPrivacy')
const acceptGuideline = field('acceptGuideline')
const authorizeSendOnBehalf = field('authorizeSendOnBehalf')
const contactViaEmail = field('contactViaEmail')
const contactViaWhatsapp = field('contactViaWhatsapp')
const acceptConfirmData = field('acceptConfirmData')

const requiresAgb = computed(() => {
  if (props.company?.licenseAgreement?.acceptedAt) return false
  if (!isFeatureActive(featureNames.DATA_PRIVACY)) return false
  const feature = getFeature(featureNames.DATA_PRIVACY)
  return !!(feature?.config?.hasConditions ?? feature?.hasConditions)
})

const requiresGuideline = computed(() => isFeatureActive(featureNames.MATERIAL_GUIDE))

const guidelineUrl = computed(() => {
  if (!requiresGuideline.value) return null
  const feature = getFeature(featureNames.MATERIAL_GUIDE)
  if (!feature?.config) return null
  return getMaterialGuideUrl({
    config: feature.config,
    locale: locale.value,
    isSmg: !!isPlatformSmg.value,
  })
})

const guidelineReceivedSuffix = computed(() => {
  // EN leaves this empty ("I have received the …"); DE appends "erhalten".
  // `te` checks the active locale only, so a missing key can't fall back to DE "erhalten".
  if (!te('onboarding.submit.received')) return ''
  const s = t('onboarding.submit.received')
  return typeof s === 'string' && s.trim() ? s.trim() : ''
})

const privacyEndSuffix = computed(() => {
  if (!te('onboarding.submit.acceptPrivacyEnd')) return ''
  const s = t('onboarding.submit.acceptPrivacyEnd')
  return typeof s === 'string' && s.trim() ? s.trim() : ''
})

/** All required consents ticked — mirrors index.vue's reviewAcceptancesValid. */
const allRequiredAccepted = computed(() => {
  const agbOk = !requiresAgb.value || acceptAgb.value
  const guidelineOk = !requiresGuideline.value || acceptGuideline.value
  const marketOk = props.isUsOnboarding ? authorizeSendOnBehalf.value : acceptPrivacy.value
  return agbOk && marketOk && guidelineOk && acceptConfirmData.value
})

const showSupportHint = computed(
  () => allRequiredAccepted.value && !contactViaEmail.value && !contactViaWhatsapp.value
)
</script>

<style scoped>
.onboarding-legal-consents {
  max-width: 100%;
}

.onboarding-legal-consents__label {
  opacity: 1;
  line-height: 1.4;
}

.onboarding-legal-consents__checkbox :deep(.v-label) {
  opacity: 1;
}

.onboarding-legal-consents__last {
  padding-bottom: 8px;
}
</style>
